import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

// Hand-drawn loop arrow matching the style of the attachment
function HandDrawnLoopArrow() {
  return (
    <svg
      width="280"
      height="180"
      viewBox="0 0 280 180"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="select-none pointer-events-none"
    >
      <motion.path
        d="M260,110 C180,85 165,140 140,130 C105,115 100,50 145,55 C195,60 160,15 85,10 C45,6 15,22 5,25"
        stroke="#2B170A"
        strokeWidth="1.6"
        strokeLinecap="round"
        fill="none"
        opacity="0.65"
        initial={{ pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.2, ease: "easeOut" }}
      />
      <motion.path
        d="M16,14 L2,26 L18,32"
        stroke="#2B170A"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
        opacity="0.65"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 1.0, duration: 0.3 }}
      />
    </svg>
  );
}

// 4 high-fidelity cup themes with images matching the flavor section
const CUP_THEMES = [
  {
    id: "green",
    name: "Forest Green",
    src: "https://cdn.jiro.build/Laounge%20Coffe/images/Cap2.png",
    textColor: "#C7A783",
    bgColor: "#EFE7DB",
  },
  {
    id: "red",
    name: "Canyon Crimson",
    src: "https://cdn.jiro.build/Laounge%20Coffe/images/Cap1.png",
    textColor: "#E2D5C3",
    bgColor: "#FAF5EC",
  },
  {
    id: "orange",
    name: "Amber Gold",
    src: "https://cdn.jiro.build/Laounge%20Coffe/images/Cap3.png",
    textColor: "#FCECD4",
    bgColor: "#FAF5EC",
  },
  {
    id: "black",
    name: "Obsidian Black",
    src: "https://cdn.jiro.build/Laounge%20Coffe/images/Cap4.png",
    textColor: "#937356",
    bgColor: "#FAF5EC",
  },
];

export default function CoffeeVarient({ className }) {
  const [activeTheme, setActiveTheme] = useState(CUP_THEMES[0]);

  // Subtle repeating background coffee bean pattern SVG
  const coffeeBeanSvg = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='260' height='260' viewBox='0 0 260 260'%3E%3Cg fill='none' stroke='%238C4F2B' stroke-width='1.2' stroke-linecap='round' stroke-linejoin='round' stroke-opacity='0.06'%3E%3Cpath d='M180,45 L205,45 L210,75 L175,75 Z' /%3E%3Cpath d='M175,50 H165 C160,50 160,70 165,70 H175' /%3E%3Cpath d='M192,45 V41 C192,39 198,39 198,41 V45' /%3E%3Cpath d='M210,68 Q222,68 220,53 Q218,42 208,46' /%3E%3Cpath d='M40,75 C60,75 80,75 80,85 C80,90 40,90 40,85 Z' /%3E%3Cpath d='M45,60 Q60,78 75,60 Z' /%3E%3Cpath d='M45,60 H75' /%3E%3Cpath d='M75,63 C80,63 81,71 75,71' /%3E%3Cpath d='M55,52 Q58,47 55,42 M65,52 Q68,47 65,42' /%3E%3Cpath d='M20,25 C15,18 28,12 32,18 C36,24 25,32 20,25 Z' /%3E%3Cpath d='M17,21 Q25,20 33,18' /%3E%3Cpath d='M110,35 C115,28 128,32 125,39 C122,46 110,42 110,35 Z' /%3E%3Cpath d='M110,38 Q118,36 125,35' /%3E%3Cpath d='M140,210 C145,203 158,207 155,214 C152,221 140,217 140,210 Z' /%3E%3Cpath d='M140,213 Q148,211 155,210' /%3E%3Cpath d='M220,110 C215,103 228,97 232,103 C236,109 225,117 220,110 Z' /%3E%3Cpath d='M217,106 Q225,105 233,103' /%3E%3Cpath d='M35,130 L75,130 L59,155 H51 Z' /%3E%3Cpath d='M50,155 H60 L62,159 V167 L60,171 H50 L48,167 V159 Z' /%3E%3Cpath d='M55,155 V171' /%3E%3Cpath d='M51,171 L38,198 C36,204 42,210 50,210 H60 C68,210 74,204 72,198 L59,171 Z' /%3E%3Cpath d='M42,190 H68' /%3E%3Cpath d='M180,145 V195 C180,200 205,200 205,195 V145 Z' /%3E%3Cpath d='M180,150 H170 V185 H180' /%3E%3Cpath d='M177,145 H208 V142 H177 Z' /%3E%3Cpath d='M192.5,142 V128' /%3E%3Cpath d='M192.5,124 A 4 4 0 1 1 192.5,125' /%3E%3Cpath d='M180,170 H205' /%3E%3Cpath d='M120,110 L140,110 L135,150 L125,150 Z' /%3E%3Cpath d='M118,110 H142 V106 H118 Z' /%3E%3Cpath d='M130,106 L136,88' /%3E%3Cpath d='M122,122 h6 v6 h-6 Z M130,134 h6 v6 h-6 Z' /%3E%3Cpath d='M110,180 L113,170 L110,160 L115,167 L125,163 L118,172 L124,180 L115,176 Z' /%3E%3Cpath d='M230,170 L234,163 L230,156 L235,161 L243,158 L238,165 L242,172 L235,168 Z' /%3E%3Ccircle cx='95' cy='115' r='1.5' fill='%238C4F2B' fill-opacity='0.06' stroke='none' /%3E%3Ccircle cx='105' cy='120' r='1' fill='%238C4F2B' fill-opacity='0.06' stroke='none' /%3E%3Ccircle cx='160' cy='95' r='1.5' fill='%238C4F2B' fill-opacity='0.06' stroke='none' /%3E%3Ccircle cx='80' cy='30' r='1' fill='%238C4F2B' fill-opacity='0.06' stroke='none' /%3E%3Ccircle cx='160' cy='155' r='1' fill='%238C4F2B' fill-opacity='0.06' stroke='none' /%3E%3Ccircle cx='200' cy='105' r='1.5' fill='%238C4F2B' fill-opacity='0.06' stroke='none' /%3E%3C/g%3E%3C/svg%3E";

  return (
    <>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap" rel="stylesheet" />

      <section
        id="select-coffe-section"
        className={"relative w-full py-24 md:py-32 flex flex-col items-center justify-center overflow-hidden " + (className || "")}
        style={{
          backgroundColor: "#FAF4EA",
          backgroundImage: 'url("' + coffeeBeanSvg + '")',
          backgroundRepeat: "repeat",
          fontFamily: '"Cabinet Grotesk", "Inter", sans-serif',
        }}
      >
        {/* Aesthetic glowing background details */}
        <div className="absolute top-1/4 left-10 w-[500px] h-[500px] bg-[#8C4F2B]/3 rounded-full blur-[140px] pointer-events-none z-0" />
        <div className="absolute bottom-1/4 right-10 w-[500px] h-[500px] bg-[#2B170A]/3 rounded-full blur-[140px] pointer-events-none z-0" />

        {/* Main Container */}
        <div className="relative z-10 w-full max-w-[1400px] px-6 sm:px-12 flex flex-col items-center justify-center gap-[5px]">

          {/* Header Layout */}
          <div
            className="w-full text-center"
            style={{
              width: "1300px",
              maxWidth: "100%",
              paddingTop: "40px",
            }}
            id="select-coffe-header"
          >
            <h2
              style={{
                color: "#2B170A",
                textAlign: "center",
                fontFamily: '"Cabinet Grotesk", "Inter", sans-serif',
                fontSize: "124px",
                fontStyle: "normal",
                fontWeight: 800,
                lineHeight: "124px",
                letterSpacing: "-0.01em",
              }}
              className="uppercase select-none font-extrabold hidden lg:block"
            >
              FRESHLY BREWED<br />
              MOMENTS START HERE
            </h2>
            {/* Responsive fallback for smaller screens */}
            <h2
              style={{
                color: "#2B170A",
                textAlign: "center",
                fontFamily: '"Cabinet Grotesk", "Inter", sans-serif',
                fontSize: "clamp(44px, 9vw, 84px)",
                fontStyle: "normal",
                fontWeight: 800,
                lineHeight: "1.1",
                letterSpacing: "-0.01em",
              }}
              className="uppercase select-none font-extrabold lg:hidden"
            >
              FRESHLY BREWED<br />
              MOMENTS START HERE
            </h2>
          </div>

          {/* 3-Column Premium Layout - Using stable 3-column grid */}
          <div className="w-full max-w-[1300px] grid grid-cols-1 lg:grid-cols-3 items-end gap-12 mt-0 relative mx-auto">

            {/* LEFT COLUMN: Human Crafted Slogan Text */}
            <div className="w-full flex justify-center lg:justify-start lg:mb-12">
              <div className="max-w-[280px] text-center lg:text-left">
                <p
                  style={{ color: "#2B170A", fontSize: "20px", lineHeight: "21.875px" }}
                  className="font-extrabold tracking-widest uppercase select-none text-center lg:text-left"
                >
                  FROM BEAN TO BREW,<br />
                  CRAFTED TO PERFECTION
                </p>
              </div>
            </div>

            {/* MIDDLE COLUMN: Main Large Interactive Cup - PERFECTLY CENTERED */}
            <div className="relative w-full flex flex-col items-center justify-center flex-shrink-0">
              <motion.div
                style={{
                  width: "463px",
                  height: "668px",
                  aspectRatio: "61/88",
                }}
                className="relative flex items-center justify-center max-w-full"
                initial={{ scale: 0.96, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ type: "spring", stiffness: 90, damping: 20 }}
              >
                {/* Ground shadow beneath the cup */}
                <div
                  className="absolute bottom-[-16px] left-1/2 -translate-x-1/2 w-[340px] h-[30px] rounded-full blur-[2px] pointer-events-none"
                  style={{
                    background: "radial-gradient(ellipse at center, rgba(43,23,10,0.18) 0%, rgba(43,23,10,0) 70%)",
                    zIndex: 1,
                  }}
                />

                {/* Transition animations on theme change */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeTheme.id}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -20, opacity: 0 }}
                    transition={{ duration: 0.45, ease: "easeInOut" }}
                    className="absolute left-1/2 -translate-x-1/2 flex items-center justify-center z-10"
                    style={{
                      width: "473px",
                      height: "678px",
                      top: "-40px",
                    }}
                  >
                    <img
                      src={activeTheme.src}
                      alt={activeTheme.name}
                      referrerPolicy="no-referrer"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                      className="select-none pointer-events-none drop-shadow-[0_35px_50px_rgba(43,23,10,0.22)]"
                    />
                  </motion.div>
                </AnimatePresence>
              </motion.div>
            </div>

            {/* Curved Hand-drawn loop arrow pointing from grid to large cup */}
            <div className="absolute right-[20%] translate-x-[15px] top-[20%] pointer-events-none hidden xl:block select-none z-20">
              <HandDrawnLoopArrow />
            </div>

            {/* RIGHT COLUMN: selector buttons + slogan details */}
            <div className="w-full flex flex-col items-center lg:items-start gap-8 lg:mb-12 relative z-10">

              {/* 4 Horizontal selector buttons - Overlaps cup on the right */}
              <div className="flex flex-row gap-3 sm:gap-4 items-center justify-center lg:justify-start flex-wrap lg:flex-nowrap lg:relative lg:-left-16 z-30">
                {CUP_THEMES.map((theme) => {
                  const isActive = theme.id === activeTheme.id;
                  return (
                    <motion.button
                      key={theme.id}
                      onClick={() => setActiveTheme(theme)}
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        width: "112px",
                        height: "162px",
                        padding: "24px 18px 0 19px",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: isActive ? "#EFE7DB" : "#FAF5EC",
                      }}
                      className="rounded-[48px] cursor-pointer shadow-sm hover:shadow-md transition-all duration-300 relative group"
                    >
                      {/* Inside box image layout */}
                      <div
                        style={{
                          width: "75px",
                          height: "108px",
                          flexShrink: 0,
                          aspectRatio: "25/36",
                        }}
                        className="flex items-center justify-center overflow-visible mb-4"
                      >
                        <img
                          src={theme.src}
                          alt={theme.name}
                          referrerPolicy="no-referrer"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                          }}
                          className={"transform group-hover:scale-105 transition-transform duration-300 select-none pointer-events-none " + (isActive ? "scale-105" : "")}
                        />
                      </div>
                      {/* Small dot indicator for active cup */}
                      {isActive && (
                        <motion.div
                          layoutId="activeDot"
                          className="absolute bottom-3 w-1.5 h-1.5 rounded-full"
                          style={{ backgroundColor: "#2B170A" }}
                          transition={{ type: "spring", stiffness: 300, damping: 30 }}
                        />
                      )}
                    </motion.button>
                  );
                })}
              </div>

              {/* Slogan details */}
              <div className="text-center lg:text-left max-w-[280px]">
                <h4
                  style={{ color: "#2B170A", fontSize: "20px" }}
                  className="font-black uppercase tracking-wider mb-2 select-none text-center lg:text-left"
                >
                  SELECT YOUR VIBE
                </h4>
                <p
                  style={{ color: "#2B170A", fontSize: "16px" }}
                  className="font-medium opacity-75 leading-relaxed select-none text-center lg:text-left"
                >
                  At Beanro, Every Cup Begins With Carefully Selected Beans.
                </p>
              </div>

            </div>

          </div>

        </div>
      </section>
    </>
  );
}